import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
// eslint-disable-next-line max-len
import { type ConfiguratorModuleEmailContentSpecsFragment } from '../../../../api/fragments/ConfiguratorModuleEmailContentSpecs';
import { type UploadFileWithPreviewFormDataFragment } from '../../../../api/fragments/UploadFileWithPreviewFormData';
import {
    DeleteConfiguratorModuleAssetDocument,
    type DeleteConfiguratorModuleAssetMutation,
    type DeleteConfiguratorModuleAssetMutationVariables,
} from '../../../../api/mutations/deleteConfiguratorModuleAsset';
import {
    UploadConfiguratorModuleAssetDocument,
    type UploadConfiguratorModuleAssetMutation,
    type UploadConfiguratorModuleAssetMutationVariables,
} from '../../../../api/mutations/uploadConfiguratorModuleAsset';
import { ConfiguratorModuleAsset, type EmailContentUpdateType } from '../../../../api/types';
import { type ConfiguratorEmailFormValues } from './type';

type UploadParams = {
    moduleId: string;
    upload: File | UploadFileWithPreviewFormDataFragment;
    type: ConfiguratorModuleAsset;
    emailContentUpdateType: EmailContentUpdateType;
    dealerId?: string;
};

const useHandleConfiguratorModuleAssets = () => {
    const apolloClient = useApolloClient();

    const updateOrDelete = useCallback(
        (
            { type, moduleId, upload, emailContentUpdateType, dealerId }: UploadParams,
            initialValue: UploadFileWithPreviewFormDataFragment
        ) => {
            const uploads = [];

            if (upload instanceof File) {
                // upload a new file
                uploads.push(
                    apolloClient.mutate<
                        UploadConfiguratorModuleAssetMutation,
                        UploadConfiguratorModuleAssetMutationVariables
                    >({
                        mutation: UploadConfiguratorModuleAssetDocument,
                        variables: {
                            moduleId,
                            type,
                            upload,
                            emailContentUpdateType,
                            dealerId,
                        },
                    })
                );
            } else if (upload === null && isObject(initialValue)) {
                // delete existing file
                uploads.push(
                    apolloClient.mutate<
                        DeleteConfiguratorModuleAssetMutation,
                        DeleteConfiguratorModuleAssetMutationVariables
                    >({
                        mutation: DeleteConfiguratorModuleAssetDocument,
                        variables: { moduleId, type, emailContentUpdateType, dealerId },
                    })
                );
            }

            return uploads;
        },
        [apolloClient]
    );

    return useCallback(
        (
            moduleId: string,
            values: ConfiguratorEmailFormValues,
            emailContentUpdateType: EmailContentUpdateType,
            initialValues?: ConfiguratorModuleEmailContentSpecsFragment,
            dealerId?: string
        ) => {
            const { saveOrder, submitOrder } = values;

            const uploads = [
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: saveOrder.bannerImage,
                        type: ConfiguratorModuleAsset.SaveOrderBanner,
                        emailContentUpdateType,
                        dealerId,
                    },
                    initialValues?.saveOrder.bannerImage
                ),
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: saveOrder.introImage,
                        type: ConfiguratorModuleAsset.SaveOrderIntro,
                        emailContentUpdateType,
                        dealerId,
                    },
                    initialValues?.saveOrder.introImage
                ),
                ...updateOrDelete(
                    {
                        moduleId,
                        upload: submitOrder.introImage,
                        type: ConfiguratorModuleAsset.SubmitOrderIntro,
                        emailContentUpdateType,
                        dealerId,
                    },
                    initialValues?.submitOrder.introImage
                ),
            ];

            if (uploads.length) {
                return Promise.all(uploads);
            }

            return Promise.resolve([]);
        },
        [updateOrDelete]
    );
};

export default useHandleConfiguratorModuleAssets;
